/** Stock Summary, Day Book, Sales Register and Search — the remaining Tier 1 screens. */
import { useMemo, useState } from "react";

import { Card, Empty, Money, Row, Rows, inputClass } from "../components/ui.js";
import {
  deriveDayBook,
  deriveSalesRegister,
  deriveStockSummary,
  formatDate,
  formatMoney,
  formatQty,
  search,
} from "../derive/reports.js";
import { Delta, PeriodPicker } from "../components/PeriodPicker.js";
import { ShareBar } from "../components/ShareBar.js";
import { ALL_TIME, change, lastYear } from "../period.js";
import type { Period } from "../period.js";
import { navigate } from "../routes.js";
import { useApp } from "../state.js";

export function StockSummary() {
  const { snapshot } = useApp();
  const model = useMemo(() => (snapshot ? deriveStockSummary(snapshot) : null), [snapshot]);

  if (!model) return <Empty>Nothing loaded.</Empty>;
  if (model.rows.length === 0) return <Empty>This company keeps no stock.</Empty>;

  return (
    <Card title={`${model.rows.length} items · ${formatMoney(model.totalValue)}`}>
      <Rows>
        {model.rows.map((row) => (
          <Row
            key={row.name}
            title={row.name}
            sub={row.group}
            right={<Money amount={row.value} />}
            rightSub={formatQty(row.qty, row.unit)}
          />
        ))}
      </Rows>
    </Card>
  );
}

export function DayBook() {
  const { snapshot, asOf } = useApp();
  const [period, setPeriod] = useState<Period>(ALL_TIME);

  const entries = useMemo(() => {
    if (!snapshot) return [];
    const filter: { from?: string; to?: string } = {};
    if (period.from) filter.from = period.from;
    if (period.to) filter.to = period.to;
    return deriveDayBook(snapshot, filter);
  }, [snapshot, period]);

  if (!snapshot) return <Empty>Nothing loaded.</Empty>;

  return (
    <div className="space-y-4">
      <PeriodPicker today={asOf} value={period} onChange={setPeriod} />

      <ShareBar
        csv={[
          {
            label: "Export CSV",
            filename: "day-book",
            build: () => [
              ["Date", "Voucher type", "Voucher no", "Party", "Narration", "Amount"],
              ...entries.map((e) => [
                e.date,
                e.voucherType,
                e.voucherNumber,
                e.party,
                e.narration ?? "",
                e.amount.toFixed(2),
              ]),
            ],
          },
        ]}
      />

      <Card title={`${entries.length} vouchers`}>
        {entries.length === 0 ? (
          <Empty>Nothing in this range.</Empty>
        ) : (
          <Rows>
            {entries.slice(0, 300).map((entry) => (
              <Row
                key={entry.id}
                title={`${entry.voucherType} ${entry.voucherNumber}`}
                sub={entry.party || entry.narration}
                right={<Money amount={entry.amount} />}
                rightSub={formatDate(entry.date)}
                // Open the VOUCHER, not the party's ledger. Tapping an invoice
                // should show what was sold and at what price.
                onClick={() => navigate({ id: "voucher", param: entry.id })}
              />
            ))}
          </Rows>
        )}
      </Card>
    </div>
  );
}

export function SalesRegister() {
  const { snapshot, groups, asOf } = useApp();
  const [period, setPeriod] = useState<Period>(ALL_TIME);

  const model = useMemo(
    () =>
      snapshot && groups
        ? deriveSalesRegister(snapshot, groups, { from: period.from, to: period.to })
        : null,
    [snapshot, groups, period],
  );

  /*
   * The same months a year earlier, keyed by month so each row can find its
   * counterpart. Derived from the WHOLE snapshot rather than the selected
   * period, because the comparison window sits outside that period by
   * definition.
   */
  const priorByMonth = useMemo(() => {
    if (!snapshot || !groups) return new Map<string, number>();
    const previous = lastYear(period);
    const prior = deriveSalesRegister(snapshot, groups, {
      from: previous.from,
      to: previous.to,
    });
    return new Map(prior.months.map((m) => [m.month, m.amount]));
  }, [snapshot, groups, period]);

  const priorTotal = useMemo(
    () => [...priorByMonth.values()].reduce((sum, amount) => sum + amount, 0),
    [priorByMonth],
  );

  if (!model) return <Empty>Nothing loaded.</Empty>;

  return (
    <div className="space-y-4">
      <PeriodPicker today={asOf} value={period} onChange={setPeriod} />

      {model.entries.length === 0 ? (
        <Empty>No sales in this period.</Empty>
      ) : (
        <>
      <Card
        title={`Total ${formatMoney(model.total)}`}
        action={
          period.id !== "all" && priorTotal > 0 ? (
            <span className="flex items-baseline gap-1.5 text-xs text-ink-3">
              vs {formatMoney(priorTotal)} last year
              <Delta percent={change(model.total, priorTotal)} />
            </span>
          ) : undefined
        }
      >
        <Rows>
          {model.months.map((month) => {
            // Same calendar month, previous year. A month absent from the prior
            // year is a genuine zero, not a missing value — but `change()`
            // returns null on a zero base, so no misleading percentage appears.
            const prior = priorByMonth.get(shiftYear(month.month));
            return (
              <Row
                key={month.month}
                title={month.label}
                sub={`${month.count} invoices`}
                right={<Money amount={month.amount} />}
                rightSub={
                  prior === undefined ? undefined : <Delta percent={change(month.amount, prior)} />
                }
              />
            );
          })}
        </Rows>
      </Card>

      <ShareBar
        csv={[
          {
            label: "Export CSV",
            filename: "sales-register",
            build: () => [
              ["Date", "Voucher type", "Voucher no", "Party", "Amount"],
              ...model.entries.map((e) => [
                e.date,
                e.voucherType,
                e.voucherNumber,
                e.party,
                e.amount.toFixed(2),
              ]),
            ],
          },
        ]}
      />

      <Card title="Invoices">
        <Rows>
          {model.entries.slice(0, 300).map((entry) => (
            <Row
              key={entry.id}
              title={`${entry.voucherType} ${entry.voucherNumber}`}
              sub={entry.party || entry.narration}
              right={<Money amount={entry.amount} />}
              rightSub={formatDate(entry.date)}
              onClick={() => navigate({ id: "voucher", param: entry.id })}
            />
          ))}
        </Rows>
      </Card>
        </>
      )}
    </div>
  );
}

/** "2026-07" → "2025-07". */
function shiftYear(month: string): string {
  const [year, m] = month.split("-");
  return `${Number(year) - 1}-${m}`;
}

export function Search() {
  const { snapshot } = useApp();
  const [query, setQuery] = useState("");

  const hits = useMemo(() => (snapshot ? search(snapshot, query) : []), [snapshot, query]);

  return (
    <div className="space-y-4">
      <input
        className={inputClass}
        placeholder="Party, item, voucher number, narration"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />

      {query.trim().length < 2 ? (
        <Empty>Type at least two characters.</Empty>
      ) : hits.length === 0 ? (
        <Empty>Nothing matches “{query}”.</Empty>
      ) : (
        <Card title={`${hits.length} results`}>
          <Rows>
            {hits.map((hit, i) => (
              <Row
                key={`${hit.kind}-${hit.label}-${i}`}
                title={hit.label}
                sub={`${hit.kind}${hit.sub ? ` · ${hit.sub}` : ""}`}
                right={<Money amount={hit.balance} />}
                rightSub={hit.kind === "voucher" ? formatDate(hit.date) : undefined}
                onClick={
                  hit.kind === "ledger"
                    ? () => navigate({ id: "ledger", param: hit.label })
                    : hit.kind === "voucher"
                      ? () => navigate({ id: "voucher", param: hit.id })
                      : undefined
                }
              />
            ))}
          </Rows>
        </Card>
      )}
    </div>
  );
}
